import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid } from "lucide-react";
import { PivotFormatEditor } from "@/components/bi/PivotFormatEditor";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}

// Pivot options (items 78-98): subtotals, grand totals, value display, sorting, heatmap.
export function PivotEditor({ opts, patchOpts, config = {} }) {
  const p = opts.pivotOpts || {};
  const set = (patch) => patchOpts({ pivotOpts: { ...p, ...patch } });
  const measureKeys = (config.measures || []).map((m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`);
  return (
    <div data-testid="pivot-editor" className="space-y-2">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><LayoutGrid className="w-3 h-3" />Pivot options</Label>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Layout</Label>
        <Select value={p.layout || "compact"} onValueChange={(v) => set({ layout: v === "compact" ? "" : v, ...(v === "compact" ? { repeatLabels: false } : {}) })}>
          <SelectTrigger data-testid="pivot-layout" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="compact">Compact (single hierarchy column)</SelectItem>
            <SelectItem value="classic">Outline / Classic (column per field)</SelectItem>
            <SelectItem value="tabular">Tabular (Excel style, subtotals below)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {(p.layout === "classic" || p.layout === "tabular") && (
        <Row label="Repeating labels"><Switch data-testid="pivot-repeat-labels" checked={!!p.repeatLabels} onCheckedChange={(v) => set({ repeatLabels: v })} /></Row>
      )}
      <Row label="Grouping bar (edit mode)"><Switch data-testid="pivot-grouping-bar" checked={p.groupingBar !== false} onCheckedChange={(v) => set({ groupingBar: v })} /></Row>
      <Row label="Row subtotals"><Switch data-testid="pivot-row-subtotals" checked={p.rowSubtotals !== false} onCheckedChange={(v) => set({ rowSubtotals: v })} /></Row>
      <Row label="Column subtotals"><Switch data-testid="pivot-col-subtotals" checked={!!p.colSubtotals} onCheckedChange={(v) => set({ colSubtotals: v })} /></Row>
      <Row label="Grand total row"><Switch data-testid="pivot-grand-row" checked={p.grandRow !== false} onCheckedChange={(v) => set({ grandRow: v })} /></Row>
      <Row label="Grand total column"><Switch data-testid="pivot-grand-col" checked={p.grandCol !== false} onCheckedChange={(v) => set({ grandCol: v })} /></Row>
      <Row label="Start collapsed"><Switch data-testid="pivot-collapsed" checked={!!p.collapsed} onCheckedChange={(v) => set({ collapsed: v })} /></Row>
      <Row label="Wrap text"><Switch data-testid="pivot-wrap" checked={!!p.wrap} onCheckedChange={(v) => set({ wrap: v })} /></Row>
      {p.wrap && <p className="text-[9px] text-[color:var(--ink-3)] -mt-1">Rows grow to fit wrapped labels and values (row virtualization pauses).</p>}
      <Row label="Compact density"><Switch data-testid="pivot-dense" checked={!!p.dense} onCheckedChange={(v) => set({ dense: v })} /></Row>
      <Row label="Striped rows"><Switch data-testid="pivot-striped" checked={!!p.striped} onCheckedChange={(v) => set({ striped: v })} /></Row>
      <Row label="Freeze first column"><Switch data-testid="pivot-freeze-col" checked={p.freezeFirstCol !== false} onCheckedChange={(v) => set({ freezeFirstCol: v })} /></Row>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Empty cells (item 84)</Label>
        <Select value={p.emptyMode || "default"} onValueChange={(v) => set({ emptyMode: v === "default" ? "" : v })}>
          <SelectTrigger data-testid="pivot-empty-mode" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default (—)</SelectItem>
            <SelectItem value="blank">Show blank</SelectItem>
            <SelectItem value="zero">Replace with 0</SelectItem>
            <SelectItem value="dash">Replace with "-"</SelectItem>
            <SelectItem value="na">Replace with N/A</SelectItem>
            <SelectItem value="custom">Custom text…</SelectItem>
          </SelectContent>
        </Select>
        {p.emptyMode === "custom" && (
          <Input data-testid="pivot-empty-custom" placeholder="e.g. n/a" value={(p.valuesFmt || {}).nullDisplay ?? ""}
            onChange={(e) => set({ valuesFmt: { ...(p.valuesFmt || {}), nullDisplay: e.target.value } })} className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]" />
        )}
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Min column width (px)</Label>
        <Select value={String(p.colMinWidth || 0)} onValueChange={(v) => set({ colMinWidth: Number(v) })}>
          <SelectTrigger data-testid="pivot-col-width" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Auto</SelectItem>
            {[60, 80, 100, 120, 160, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}px</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Show values as</Label>
        <Select value={p.valueAs || "raw"} onValueChange={(v) => set({ valueAs: v })}>
          <SelectTrigger data-testid="pivot-value-as" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="raw">Values</SelectItem>
            <SelectItem value="pct_row">% of row total</SelectItem>
            <SelectItem value="pct_col">% of column total</SelectItem>
            <SelectItem value="pct_grand">% of grand total</SelectItem>
            <SelectItem value="diff">Difference from previous</SelectItem>
            <SelectItem value="running">Running total</SelectItem>
          </SelectContent>
        </Select>
        {(p.valueAs === "diff" || p.valueAs === "running") && (
          <Select value={p.valueAsDir || "down"} onValueChange={(v) => set({ valueAsDir: v })}>
            <SelectTrigger data-testid="pivot-value-as-dir" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="down">Down the rows</SelectItem>
              <SelectItem value="across">Across the columns</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Sort rows by</Label>
          <Select value={p.sortBy || "label"} onValueChange={(v) => set({ sortBy: v })}>
            <SelectTrigger data-testid="pivot-sort-by" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="label">Label</SelectItem>
              <SelectItem value="value">Measure value</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Direction</Label>
          <Select value={p.sortDir || "asc"} onValueChange={(v) => set({ sortDir: v })}>
            <SelectTrigger data-testid="pivot-sort-dir" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <LevelSortEditor p={p} set={set} config={config} measureKeys={measureKeys} />
      <ColLevelSortEditor p={p} set={set} config={config} measureKeys={measureKeys} />
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Row label="Heatmap cells"><Switch data-testid="pivot-heatmap" checked={!!p.heatmap} onCheckedChange={(v) => set({ heatmap: v })} /></Row>
        {p.heatmap && (
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-[color:var(--ink-3)]">Color</Label>
            <input data-testid="pivot-heat-color" type="color" value={p.heatColor || "#2B4EFF"} onChange={(e) => set({ heatColor: e.target.value })}
              className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          </div>
        )}
      </div>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Top N rows</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <Input data-testid="pivot-topn-n" type="number" min={0} value={p.topN?.n ?? ""} placeholder="off"
            onChange={(e) => set({ topN: { ...(p.topN || {}), n: e.target.value === "" ? 0 : Number(e.target.value), measure: p.topN?.measure || measureKeys[0], dir: p.topN?.dir || "desc" } })}
            className="h-7 text-xs bg-[color:var(--surface-2)]" />
          <Select value={p.topN?.dir || "desc"} onValueChange={(v) => set({ topN: { ...(p.topN || {}), dir: v } })}>
            <SelectTrigger data-testid="pivot-topn-dir" className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="desc">Top</SelectItem><SelectItem value="asc">Bottom</SelectItem></SelectContent>
          </Select>
        </div>
        {measureKeys.length > 1 && (
          <Select value={p.topN?.measure || measureKeys[0] || ""} onValueChange={(v) => set({ topN: { ...(p.topN || {}), measure: v } })}>
            <SelectTrigger data-testid="pivot-topn-measure" className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="by measure" /></SelectTrigger>
            <SelectContent>{measureKeys.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Value filter</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <Select value={p.valueFilter?.op || "__none__"} onValueChange={(v) => set({ valueFilter: v === "__none__" ? null : { ...(p.valueFilter || {}), op: v, measure: p.valueFilter?.measure || measureKeys[0] } })}>
            <SelectTrigger data-testid="pivot-vf-op" className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Off</SelectItem>
              <SelectItem value="gt">Greater than</SelectItem>
              <SelectItem value="gte">At least</SelectItem>
              <SelectItem value="lt">Less than</SelectItem>
              <SelectItem value="lte">At most</SelectItem>
              <SelectItem value="eq">Equals</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="pivot-vf-value" type="number" value={p.valueFilter?.value ?? ""} placeholder="value" disabled={!p.valueFilter?.op}
            onChange={(e) => set({ valueFilter: { ...(p.valueFilter || {}), value: e.target.value === "" ? null : Number(e.target.value) } })}
            className="h-7 text-xs bg-[color:var(--surface-2)]" />
        </div>
        {measureKeys.length > 1 && p.valueFilter?.op && (
          <Select value={p.valueFilter?.measure || measureKeys[0] || ""} onValueChange={(v) => set({ valueFilter: { ...(p.valueFilter || {}), measure: v } })}>
            <SelectTrigger data-testid="pivot-vf-measure" className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>{measureKeys.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <p className="text-[9px] text-[color:var(--ink-3)]">Top N and value filters apply to the first row field's totals (sum-based).</p>
      </div>
      <p className="text-[10px] text-[color:var(--ink-3)]">
        Assign dimensions to Rows/Columns with the <b>Row/Col</b> toggle in the Data section.
        The processing engine (client vs server) is set per dashboard in Canvas settings.
      </p>
      <PivotFormatEditor opts={opts} patchOpts={patchOpts} config={config} />
    </div>
  );
}

// Item 6: the same Ascending / Descending / Inherit / Manual choice for each column level.
function ColLevelSortEditor({ p, set, config, measureKeys }) {
  const colFields = (config.dimensions || [])
    .filter((d) => d && (d.key || d.field) && d.axis === "col")
    .map((d) => d.key || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field));
  if (!colFields.length) return null;
  const cs = p.colLevelSort || {};
  const setLevel = (i, patch) => {
    const cur = cs[i] || {};
    const next = { ...cs, [i]: { ...cur, ...patch } };
    if (!next[i].by && !next[i].dir) delete next[i];
    set({ colLevelSort: next });
  };
  return (
    <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="pivot-col-level-sort">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Per-column-level sorting</Label>
      {colFields.map((f, i) => {
        const cur = cs[i] || {};
        return (
          <div key={`${f}-${i}`} className="space-y-1">
            <div className="grid grid-cols-[1fr_86px_64px] gap-1 items-center">
              <span className="text-[10px] text-[color:var(--ink-3)] truncate" title={f}>{String(f).replace(/_/g, " ")}</span>
              <Select value={cur.by || "inherit"} onValueChange={(v) => setLevel(i, { by: v === "inherit" ? "" : v })}>
                <SelectTrigger data-testid={`pivot-col-level-by-${i}`} className="h-6 text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Inherit</SelectItem>
                  <SelectItem value="label">Caption</SelectItem>
                  <SelectItem value="value">Value</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={cur.dir || "asc"} onValueChange={(v) => setLevel(i, { dir: v })}>
                <SelectTrigger data-testid={`pivot-col-level-dir-${i}`} className="h-6 text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="asc">Asc</SelectItem><SelectItem value="desc">Desc</SelectItem></SelectContent>
              </Select>
            </div>
            {cur.by === "manual" && (
              <Input data-testid={`pivot-col-level-manual-${i}`} placeholder="Order: New Delhi, Uttar Pradesh, Goa"
                value={(cur.manual || []).join(", ")}
                onChange={(e) => setLevel(i, { manual: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className="h-6 text-[10px] bg-[color:var(--surface-2)]" />
            )}
          </div>
        );
      })}
      {measureKeys.length > 1 && <p className="text-[9px] text-[color:var(--ink-3)]">Value sorting uses the pivot's sort measure.</p>}
    </div>
  );
}

// Item 84: independent sorting per hierarchy level (label / value / manual order).
function LevelSortEditor({ p, set, config, measureKeys }) {
  const rowFields = (config.dimensions || [])
    .filter((d) => d && (d.key || d.field) && (d.axis || "row") === "row")
    .map((d) => d.key || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field));
  if (rowFields.length < 1) return null;
  const ls = p.levelSort || {};
  const setLevel = (i, patch) => {
    const cur = ls[i] || {};
    const next = { ...ls, [i]: { ...cur, ...patch } };
    if (!next[i].by) delete next[i];
    set({ levelSort: next });
  };
  return (
    <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="pivot-level-sort">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Per-level sorting</Label>
      {rowFields.map((f, i) => {
        const cur = ls[i] || {};
        return (
          <div key={`${f}-${i}`} className="space-y-1">
            <div className="grid grid-cols-[1fr_86px_64px] gap-1 items-center">
              <span className="text-[10px] text-[color:var(--ink-3)] truncate" title={f}>{String(f).replace(/_/g, " ")}</span>
              <Select value={cur.by || "inherit"} onValueChange={(v) => setLevel(i, { by: v === "inherit" ? "" : v })}>
                <SelectTrigger data-testid={`pivot-level-by-${i}`} className="h-6 text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Inherit</SelectItem>
                  <SelectItem value="label">Caption</SelectItem>
                  <SelectItem value="value">Value</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={cur.dir || "asc"} onValueChange={(v) => setLevel(i, { dir: v })}>
                <SelectTrigger data-testid={`pivot-level-dir-${i}`} className="h-6 text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="asc">Asc</SelectItem><SelectItem value="desc">Desc</SelectItem></SelectContent>
              </Select>
            </div>
            {cur.by === "value" && measureKeys.length > 1 && (
              <Select value={cur.measure || measureKeys[0]} onValueChange={(v) => setLevel(i, { measure: v })}>
                <SelectTrigger data-testid={`pivot-level-measure-${i}`} className="h-6 text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>{measureKeys.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {cur.by === "manual" && (
              <Input data-testid={`pivot-level-manual-${i}`} placeholder="Order: North, South, East, West"
                value={(cur.manual || []).join(", ")}
                onChange={(e) => setLevel(i, { manual: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className="h-6 text-[10px] bg-[color:var(--surface-2)]" />
            )}
          </div>
        );
      })}
      <p className="text-[9px] text-[color:var(--ink-3)]">Manual: values listed first appear in that order; the rest follow alphabetically.</p>
    </div>
  );
}
